import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  VStack,
  Textarea,
} from '@chakra-ui/react';
import DefaultAuth from 'layouts/auth/Default';
import illustration from 'assets/img/auth/auth.png';
import { MdOutlineRemoveRedEye, MdCheckCircle, MdError } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';
import { doc, updateDoc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from 'lib/firebase';
import { getAuth, signInWithEmailAndPassword, signOut } from 'firebase/auth';

function TaxInfo() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorBrand = useColorModeValue('brand.500', 'white');
  const inputBg = useColorModeValue('secondaryGray.300', 'navy.800');
  const inputText = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.300', 'whiteAlpha.100');
  const cardBg = useColorModeValue('white', 'navy.800');
  
  const toast = useToast();
  const auth = getAuth();
  
  // Local auth state (bypassing AuthContext to allow instructor login)
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isSignedIn, setIsSignedIn] = useState(false);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  
  // Tax info form state
  const [address, setAddress] = useState('');
  const [ssn, setSsn] = useState('');
  const [showSsn, setShowSsn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Check authentication status on mount
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        setIsSignedIn(true);
        
        // Fetch user document
        try {
          const userDocRef = doc(db, 'Users', firebaseUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            setUserData({ id: userDoc.id, ...userDoc.data() });
          } else {
            setUserData(null);
          }
        } catch (err) {
          console.error('Error fetching user document:', err);
          setUserData(null);
        }
        setLoading(false);
      } else {
        setUser(null);
        setIsSignedIn(false);
        setUserData(null);
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [auth]);
  
  // Load existing data when user is authenticated
  useEffect(() => {
    if (isSignedIn && userData) {
      setAddress(userData.Address || '');
      // Never load SSN from database for security - always require fresh entry
      setSsn('');
      setLoading(false);
    } else if (isSignedIn && !userData) {
      setLoading(false);
    }
  }, [isSignedIn, userData]);
  
  // Format SSN as user types (XXX-XX-XXXX)
  const handleSsnChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    
    if (value.length > 9) {
      value = value.slice(0, 9); // Limit to 9 digits
    }
    
    // Add dashes
    if (value.length > 5) {
      value = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
    } else if (value.length > 3) {
      value = `${value.slice(0, 3)}-${value.slice(3)}`;
    }
    
    setSsn(value);
  };
  
  // Mask SSN for display
  const getMaskedSsn = () => {
    const digits = ssn.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return '•'.repeat(digits.length);
    if (digits.length <= 5) return `${'•'.repeat(3)}-${'•'.repeat(digits.length - 3)}`;
    return `${'•'.repeat(3)}-${'•'.repeat(2)}-${'•'.repeat(digits.length - 5)}`;
  };
  
  // Validate form
  const validateForm = () => {
    if (!address.trim()) {
      return 'Address is required';
    }
    
    const ssnDigits = ssn.replace(/\D/g, '');
    
    // If SSN is being entered/updated, it must be exactly 9 digits
    if (ssnDigits.length > 0 && ssnDigits.length !== 9) {
      return 'SSN must be exactly 9 digits';
    }
    
    // If no SSN exists in database and field is empty, require it
    if (!userData.SSN && ssnDigits.length === 0) {
      return 'SSN is required for first-time setup';
    }
    
    return null;
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUserData(null);
      setIsSignedIn(false);
      setEmail('');
      setPassword('');
      setAddress('');
      setSsn('');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };
  
  // Handle login (direct Firebase Auth to bypass facility-only restriction)
  const handleLogin = async () => {
    setLoginError('');
    setLoggingIn(true);
    
    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      const firebaseUser = userCredential.user;
      
      // Fetch user document
      const userDocRef = doc(db, 'Users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        await auth.signOut();
        setLoginError('Account not found');
        return;
      }
      
      const data = { id: userDoc.id, ...userDoc.data() };
      
      // Verify user is an instructor
      if (data.Role !== 'Instructor') {
        await auth.signOut();
        setLoginError('Access restricted to instructors only');
        return;
      }
      
      // Auth listener will update state automatically
      
    } catch (err) {
      console.error('Login error:', err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setLoginError('Invalid email or password');
      } else {
        setLoginError(err.message || 'An error occurred during login. Please try again.');
      }
    } finally {
      setLoggingIn(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast({
        title: 'Validation error',
        description: validationError,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
    setSaving(true);
    try {
      const userRef = doc(db, 'Users', user.uid);
      const updateData = {
        Address: address.trim(),
        TaxInfoUpdatedAt: new Date(),
        TaxInfoUpdatedBy: user.email,
      };
      
      // Only update SSN if user entered one (don't overwrite with empty string)
      const ssnDigits = ssn.replace(/\D/g, '');
      if (ssnDigits.length === 9) {
        updateData.SSN = ssn;
      }
      
      await updateDoc(userRef, updateData);
      
      toast({
        title: 'Success',
        description: 'Tax information saved successfully',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      console.error('Error saving tax info:', err);
      toast({
        title: 'Error',
        description: 'Failed to save tax information. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Guard: Not authenticated - show login form
  if (!isSignedIn) {
    return (
      <DefaultAuth illustrationBackground={illustration} image={illustration}>
        <Flex
          maxW={{ base: '100%', md: 'max-content' }}
          w="100%"
          mx={{ base: 'auto', lg: '0px' }}
          me="auto"
          h="100%"
          alignItems="start"
          justifyContent="center"
          mb={{ base: '30px', md: '60px' }}
          px={{ base: '25px', md: '0px' }}
          mt={{ base: '40px', md: '14vh' }}
          flexDirection="column"
        >
          <Box me="auto">
            <Heading color={textColor} fontSize="36px" mb="10px">
              Tax Information
            </Heading>
            <Text
              mb="36px"
              ms="4px"
              color={textColorSecondary}
              fontWeight="400"
              fontSize="md"
            >
              Sign in to update your tax information for W-9 compliance
            </Text>
          </Box>
          <Flex
            zIndex="2"
            direction="column"
            w={{ base: '100%', md: '420px' }}
            maxW="100%"
            background="transparent"
            borderRadius="15px"
            mx={{ base: 'auto', lg: 'unset' }}
            me="auto"
            mb={{ base: '20px', md: 'auto' }}
          >
            {loginError && (
              <Alert status="error" mb="20px" borderRadius="8px">
                <AlertIcon />
                <AlertDescription fontSize="sm">{loginError}</AlertDescription>
              </Alert>
            )}
            
            <FormControl mb="24px">
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Email<Text color={textColorBrand}>*</Text>
              </FormLabel>
              <Input
                isRequired={true}
                variant="auth"
                fontSize="sm"
                ms={{ base: '0px', md: '0px' }}
                type="email"
                placeholder="mail@example.com"
                fontWeight="500"
                size="lg"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              />
            </FormControl>
            <FormControl mb="24px">
              <FormLabel
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                display="flex"
              >
                Password<Text color={textColorBrand}>*</Text>
              </FormLabel>
              <InputGroup size="md">
                <Input
                  isRequired={true}
                  fontSize="sm"
                  placeholder="Min. 8 characters"
                  size="lg"
                  type={showPassword ? 'text' : 'password'}
                  variant="auth"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                />
                <InputRightElement display="flex" alignItems="center" mt="4px">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={showPassword ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>
            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              onClick={handleLogin}
              isLoading={loggingIn}
              loadingText="Signing in..."
            >
              Sign In
            </Button>
          </Flex>
        </Flex>
      </DefaultAuth>
    );
  }
  
  // Guard: No user document found
  if (!userData) {
    return (
      <DefaultAuth illustrationBackground={illustration} image={illustration}>
        <Flex
          maxW={{ base: '100%', md: 'max-content' }}
          w="100%"
          mx="auto"
          h="100%"
          alignItems="center"
          justifyContent="center"
          px={{ base: '25px', md: '0px' }}
          mt={{ base: '40px', md: '0' }}
        >
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="12px"
          >
            <Icon as={MdError} boxSize="40px" mr={0} mb="12px" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Account Not Found
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              No user profile was found for your account. Please contact support at support@fitgroupusa.com.
            </AlertDescription>
            <Button
              mt={4}
              colorScheme="gray"
              size="sm"
              onClick={handleLogout}
            >
              Log Out
            </Button>
          </Alert>
        </Flex>
      </DefaultAuth>
    );
  }
  
  // Guard: Wrong role (not instructor)
  if (userData.Role !== 'Instructor') {
    return (
      <DefaultAuth illustrationBackground={illustration} image={illustration}>
        <Flex
          maxW={{ base: '100%', md: 'max-content' }}
          w="100%"
          mx="auto"
          h="100%"
          alignItems="center"
          justifyContent="center"
          px={{ base: '25px', md: '0px' }}
          mt={{ base: '40px', md: '0' }}
        >
          <Alert
            status="warning"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            borderRadius="12px"
          >
            <Icon as={MdError} boxSize="40px" mr={0} mb="12px" />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Access Restricted
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              This page is only available for instructors. Your account type is: {userData.Role}.
            </AlertDescription>
            <Button
              mt={4}
              colorScheme="gray"
              size="sm"
              onClick={handleLogout}
            >
              Log Out
            </Button>
          </Alert>
        </Flex>
      </DefaultAuth>
    );
  }
  
  // Main form - authenticated instructor
  if (loading) {
    return (
      <DefaultAuth illustrationBackground={illustration} image={illustration}>
        <Flex
          w="100%"
          h="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Spinner size="xl" color={textColorBrand} />
        </Flex>
      </DefaultAuth>
    );
  }
  
  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto" mb="20px">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Tax Information
          </Heading>
          <Text
            mb="12px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Please provide your information for W-9 tax compliance
          </Text>
          <Flex align="center" gap="12px">
            <Text
              ms="4px"
              color={textColorSecondary}
              fontWeight="500"
              fontSize="sm"
            >
              Logged in as: {user?.email}
            </Text>
            <Button
              size="xs"
              variant="outline"
              colorScheme="gray"
              onClick={handleLogout}
            >
              Log Out
            </Button>
          </Flex>
        </Box>
        
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '500px' }}
          maxW="100%"
          background={cardBg}
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
          p="30px"
          boxShadow="md"
        >
          <VStack spacing="24px" align="stretch">
            {/* Address Field */}
            <FormControl>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Address<Text color={textColorBrand}>*</Text>
              </FormLabel>
              <Textarea
                isRequired={true}
                variant="auth"
                fontSize="sm"
                ms={{ base: '0px', md: '0px' }}
                placeholder="123 Main Street, City, State, ZIP"
                fontWeight="500"
                size="lg"
                rows={3}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                bg={inputBg}
                color={inputText}
                borderColor={borderColor}
              />
              <Text fontSize="xs" color={textColorSecondary} mt="6px" ms="4px">
                Enter your complete mailing address for tax documents
              </Text>
            </FormControl>
            
            {/* SSN Field */}
            <FormControl>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Social Security Number (SSN)
                {!userData.SSN && <Text color={textColorBrand}>*</Text>}
              </FormLabel>
              {userData.SSN && (
                <Alert status="info" mb="8px" borderRadius="8px" fontSize="xs">
                  <AlertIcon boxSize="14px" />
                  <Text>SSN on file. Enter to update or leave blank to keep existing.</Text>
                </Alert>
              )}
              <InputGroup size="lg">
                <Input
                  isRequired={true}
                  fontSize="sm"
                  placeholder="XXX-XX-XXXX"
                  variant="auth"
                  type={showSsn ? 'text' : 'password'}
                  value={ssn}
                  onChange={handleSsnChange}
                  bg={inputBg}
                  color={inputText}
                  borderColor={borderColor}
                />
                <InputRightElement display="flex" alignItems="center">
                  <Icon
                    color={textColorSecondary}
                    _hover={{ cursor: 'pointer' }}
                    as={showSsn ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                    onClick={() => setShowSsn(!showSsn)}
                  />
                </InputRightElement>
              </InputGroup>
              <Text fontSize="xs" color={textColorSecondary} mt="6px" ms="4px">
                Format: 9 digits (XXX-XX-XXXX). For security, we never display existing SSNs.
              </Text>
            </FormControl>
            
            {/* Security Notice */}
            <Alert status="info" borderRadius="8px">
              <AlertIcon />
              <Box fontSize="sm">
                <Text fontWeight="600" mb="4px">Your information is secure</Text>
                <Text fontSize="xs">
                  Tax information is encrypted and stored securely. For your protection, we never display existing SSNs on this form.
                </Text>
              </Box>
            </Alert>
            
            {/* Submit Button */}
            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              onClick={handleSubmit}
              isLoading={saving}
              loadingText="Saving..."
              isDisabled={
                !address.trim() || 
                (!userData.SSN && ssn.replace(/\D/g, '').length !== 9) ||
                (ssn.length > 0 && ssn.replace(/\D/g, '').length !== 9)
              }
              leftIcon={<Icon as={MdCheckCircle} />}
            >
              Save Tax Information
            </Button>
          </VStack>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default TaxInfo;
